"use client";

import { t } from "@/lib/i18n";
import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  SearchX,
  ServerCrash,
  ShieldAlert,
  RotateCw,
  Home,
  ArrowLeft,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  LayoutDashboard,
  Shield,
  Layers,
  HelpCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export interface AppErrorViewProps {
  statusCode?: 404 | 500 | 403 | 503 | number;
  title?: string;
  message?: string;
  error?: Error & { digest?: string };
  reset?: () => void;
  backUrl?: string;
  isGlobal?: boolean;
}

const ERROR_CONFIG: Record<
  number,
  {
    badge: string;
    title: string;
    description: string;
    icon: typeof SearchX;
    color: string;
    badgeStyle: string;
    iconBg: string;
  }
> = {
  404: {
    badge: "Error 404 - Not Found",
    title: "Page Not Found",
    description:
      "The link you visited may have been moved, deleted, or the URL you entered is incorrect.",
    icon: SearchX,
    color: "from-orange-500 to-amber-500",
    badgeStyle: "bg-orange-50 text-orange-700 border-orange-200",
    iconBg: "from-orange-500 to-amber-500 text-white shadow-orange-500/30",
  },
  403: {
    badge: "Error 403 - Forbidden",
    title: "Restricted Access",
    description:
      "You do not have sufficient security permissions (RBAC) to open this page. Contact the Super Administrator if you need access.",
    icon: ShieldAlert,
    color: "from-amber-500 to-orange-600",
    badgeStyle: "bg-amber-50 text-amber-800 border-amber-200",
    iconBg: "from-amber-500 to-orange-600 text-white shadow-amber-500/30",
  },
  500: {
    badge: "Error 500 - Server Error",
    title: "A System Issue Occurred",
    description:
      "The server experienced an unexpected disruption while processing your request. Our team has logged this event for prompt handling.",
    icon: ServerCrash,
    color: "from-rose-500 to-orange-500",
    badgeStyle: "bg-rose-50 text-rose-700 border-rose-200",
    iconBg: "from-rose-500 to-orange-600 text-white shadow-rose-500/30",
  },
  503: {
    badge: "Error 503 - Service Unavailable",
    title: "Service Is Busy",
    description:
      "The system is undergoing synchronization or periodic maintenance. Please try again in a moment.",
    icon: ServerCrash,
    color: "from-orange-600 to-rose-600",
    badgeStyle: "bg-orange-50 text-orange-700 border-orange-200",
    iconBg: "from-orange-600 to-rose-600 text-white shadow-orange-600/30",
  },
};

export default function AppErrorView({
  statusCode = 404,
  title,
  message,
  error,
  reset,
  backUrl = "/crm",
  isGlobal = false,
}: AppErrorViewProps) {
  const router = useRouter();
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  const [copied, setCopied] = useState(false);

  const config = ERROR_CONFIG[statusCode] || ERROR_CONFIG[500];
  const Icon = config.icon;

  const displayTitle = title || config.title;
  const displayMessage = message || config.description;

  const copyErrorDetails = () => {
    const details = [
      `Status: ${statusCode}`,
      `Title: ${displayTitle}`,
      `Error Message: ${error?.message || "N/A"}`,
      `Digest: ${error?.digest || "N/A"}`,
      `Timestamp: ${new Date().toISOString()}`,
      `URL: ${typeof window !== "undefined" ? window.location.href : "N/A"}`,
    ].join("\n");

    if (navigator.clipboard) {
      navigator.clipboard.writeText(details);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const Content = (
    <div className="relative min-h-[calc(100vh-4rem)] w-full flex items-center justify-center p-4 sm:p-6 lg:p-10 bg-gradient-to-b from-orange-50/40 via-slate-50/60 to-amber-50/40 aibas-app-font selection:bg-orange-500 selection:text-white">
      {/* Background Radial Glow */}
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_60%_60%_at_50%_30%,rgba(249,115,22,0.15),rgba(255,255,255,0))]"></div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full max-w-2xl rounded-3xl sm:rounded-[2.5rem] border border-white/80 bg-white/75 p-6 sm:p-10 lg:p-12 shadow-2xl shadow-orange-500/10 backdrop-blur-2xl text-center relative overflow-hidden"
      >
        {/* Top Accent Gradient Line */}
        <div
          className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${config.color}`}
        ></div>

        {/* Status Badge */}
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border px-3.5 py-1 text-xs font-black uppercase tracking-wider shadow-2xs">
          <span className={`w-2 h-2 rounded-full bg-current animate-pulse`} />
          <span className={config.badgeStyle}>{config.badge}</span>
        </div>

        {/* Large Decorative Icon */}
        <div className="mb-6 flex justify-center">
          <motion.div
            initial={{ scale: 0.8, rotate: -5 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className={`w-20 h-20 sm:w-24 sm:h-24 rounded-3xl bg-gradient-to-tr ${config.iconBg} flex items-center justify-center shadow-xl`}
          >
            <Icon size={44} strokeWidth={2.2} />
          </motion.div>
        </div>

        {/* Heading & Description */}
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">
          {displayTitle}
        </h1>
        <p className="mt-3 text-xs sm:text-sm leading-relaxed text-slate-600 font-medium max-w-lg mx-auto">
          {displayMessage}
        </p>

        {/* ACTION BUTTONS */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
          {/* Retry Button (if reset handler available) */}
          {reset && (
            <button
              type="button"
              onClick={() => reset()}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 text-xs sm:text-sm font-black uppercase tracking-wider text-white shadow-lg shadow-orange-500/25 hover:scale-[1.02] active:scale-95 transition"
            >
              <RotateCw size={16} />
              <span>{t("Try Again")}</span>
            </button>
          )}

          {/* Back to Home / Dashboard Button */}
          <Link
            href={backUrl}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-2xl text-xs sm:text-sm font-black uppercase tracking-wider transition ${
              reset
                ? "border border-orange-200 bg-white/90 text-slate-700 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700 shadow-xs"
                : "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/25 hover:scale-[1.02] active:scale-95"
            }`}
          >
            <Home size={16} />
            <span>{t("Back to Home")}</span>
          </Link>

          {/* Go Back Button */}
          <button
            type="button"
            onClick={() => router.back()}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl border border-slate-200 bg-white/70 text-xs sm:text-sm font-bold text-slate-600 hover:bg-slate-50 transition"
          >
            <ArrowLeft size={15} />
            <span>{t("Previous Page")}</span>
          </button>
        </div>

        {/* TECHNICAL DETAILS TOGGLE (for 500 / Runtime Errors) */}
        {error && (
          <div className="mt-8 pt-6 border-t border-slate-100/80 text-left">
            <button
              type="button"
              onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
              className="w-full flex items-center justify-between text-xs font-bold text-slate-500 hover:text-orange-600 transition"
            >
              <span className="flex items-center gap-1.5">
                <HelpCircle size={14} />
                <span>{t("Technical Error Details (For Developers / IT)")}</span>
              </span>
              {showTechnicalDetails ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
            </button>

            <AnimatePresence>
              {showTechnicalDetails && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-3 rounded-2xl border border-slate-200 bg-slate-900 p-4 text-xs font-mono text-slate-200 space-y-2 overflow-hidden"
                >
                  <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                    <span className="text-[10px] font-bold text-slate-400 uppercase">
                      {t("Error Diagnostics")}
                    </span>
                    <button
                      type="button"
                      onClick={copyErrorDetails}
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-orange-400 hover:text-orange-300"
                    >
                      {copied ? <Check size={12} /> : <Copy size={12} />}
                      <span>{copied ? t("Copied!") : t("Copy Log")}</span>
                    </button>
                  </div>
                  <p className="text-rose-400 font-bold break-all">
                    {t(error.message) || t("Unknown error encountered")}
                  </p>
                  {error.digest && (
                    <p className="text-slate-400 text-[11px]">
                      {t("Digest:")} <span className="text-slate-300">{error.digest}</span>
                    </p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* QUICK NAVIGATION SHORTCUTS */}
        <div className="mt-8 pt-6 border-t border-slate-100/80">
          <div className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-3">
            {t("Quick Navigation")}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Link
              href="/crm"
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200/80 bg-slate-50/80 px-3 py-1.5 text-xs font-bold text-slate-700 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700 transition"
            >
              <LayoutDashboard size={13} className="text-orange-500" />
              <span>{t("CRM Dashboard")}</span>
            </Link>
            <Link
              href="/admin"
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200/80 bg-slate-50/80 px-3 py-1.5 text-xs font-bold text-slate-700 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700 transition"
            >
              <Shield size={13} className="text-orange-500" />
              <span>{t("Admin Control Center")}</span>
            </Link>
            <Link
              href="/connect"
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200/80 bg-slate-50/80 px-3 py-1.5 text-xs font-bold text-slate-700 hover:border-orange-300 hover:bg-orange-50 hover:text-orange-700 transition"
            >
              <Layers size={13} className="text-orange-500" />
              <span>{t("Task Connect")}</span>
            </Link>
          </div>
        </div>

      </motion.div>
    </div>
  );

  if (isGlobal) {
    return (
      <html lang="id">
        <body className="antialiased">{Content}</body>
      </html>
    );
  }

  return Content;
}
